import React, { useState, useCallback, useEffect } from "react";
import {
  IconButton,
  MenuItem,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@material-ui/core";
import {
  Extension,
  Assessment,
  BugReport,
  Build,
  Chat,
  Add,
} from "@material-ui/icons";
import SmartToy from "@mui/icons-material/SmartToy";
import AutoFixHigh from "@mui/icons-material/AutoFixHigh";
import { toast } from "react-toastify";
import { PluginRuntime, i18nTranslate } from "@markedesk/plugin-sdk/ui";

import { useChannelPlugins } from "../ChannelIcon";
import PluginHtmlInline from "./PluginHtmlInline";
import api from "../../services/api";
import ResultadoJsx from "./ResultadoJsx";

const iconMap = {
  SmartToy,
  Extension,
  AutoFixHigh,
  Assessment,
  BugReport,
  Build,
  Chat,
  Add,
};

/**
 * PluginSlot — componente generico interno que renderiza acoes de plugins.
 *
 * NAO use este componente diretamente. Use um dos slots dedicados em
 * `./slots/` (ex: MessageInputSlot, TicketActionsSlot). Cada slot dedicado
 * define seu nome, formato e contrato de context.
 *
 * Props:
 *   slot         — nome do slot (ex: "message-input", "ticket-actions")
 *   context      — dados do contexto (ticketId, contactId, etc.)
 *   as           — "buttons" | "menu-items"
 *   onInsertText — callback chamado quando resultType e "insert-text"
 */
/**
 * O rotulo da action no idioma da interface.
 *
 * Passa o `label` pelo dicionario do plugin (`provides.i18n`): se houver
 * traducao para aquela chave, usa; se nao, devolve o proprio texto. Assim um
 * plugin antigo, com o rotulo escrito direto no metadata, continua igual — e um
 * plugin novo declara `label: "resumirTicket"` e ganha traducao sem que o core
 * conheca a frase.
 */
function rotuloDaAction(action) {
  const pluginId = action.plugin?.id || action.plugin?.channel;
  return i18nTranslate(pluginId, action.label, PluginRuntime.idioma);
}

export default function PluginSlot({ slot, context = {}, as = "buttons", onInsertText, onPluginMessage }) {
  const plugins = useChannelPlugins();
  const [loading, setLoading] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogContent, setDialogContent] = useState("");
  const [iframeModal, setIframeModal] = useState(null); // { title, src }
  // Resultado desenhado pelo PLUGIN (resultType "jsx") — { texto, carregando }.
  // Fica acima da
  // caixa de digitacao em vez de entrar direto nela.
  const [resultadoJsx, setResultadoJsx] = useState(null);

  const renderAs = as;

  // Escuta mensagens (postMessage) vindas das páginas de plugin em iframe.
  // Permite que o painel do plugin peça ações ao app (ex: criar ticket).
  useEffect(() => {
    if (!onPluginMessage) return undefined;
    const handler = (event) => {
      const data = event?.data;
      if (data && typeof data === "object" && data.source) {
        onPluginMessage(data, () => setIframeModal(null));
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onPluginMessage]);

  // Coletar acoes de todos os plugins para este slot.
  //
  // Filtro por canal: um plugin que fornece canal (provides.channel) so mostra
  // suas actions no ticket DAQUELE canal. Sem isto, os botoes de um plugin de
  // canal (ex: comandos do echo) vazavam para tickets de todos os outros canais
  // (WhatsApp, hineken...). Plugins SEM canal (actions genericas) aparecem
  // sempre. So filtra quando o contexto informa o canal (ex: message-input);
  // slots sem canal no contexto mantem o comportamento antigo.
  const contextChannel = context?.channel;
  const actions = [];
  for (const plugin of plugins) {
    if (!plugin.provides?.actions) continue;
    const pluginChannel = plugin.provides?.channel?.channel;
    if (pluginChannel && contextChannel && pluginChannel !== contextChannel) {
      continue; // action de outro canal — nao mostra neste ticket
    }
    for (const action of plugin.provides.actions) {
      if (action.slot === slot) {
        actions.push({ ...action, plugin });
      }
    }
  }

  const handleAction = useCallback(async (action) => {
    const { id, endpoint, plugin, resultType, openType } = action;

    if (!plugin.routePath) {
      toast.error("Plugin sem rota configurada");
      return;
    }

    // openType "modal-iframe": abre a página do plugin num modal (não chama POST).
    if (openType === "modal-iframe") {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const ep = (endpoint || "").startsWith("/") ? endpoint : `/${endpoint || ""}`;
      const qs = new URLSearchParams();
      Object.keys(context || {}).forEach((k) => {
        if (context[k] != null) qs.set(k, context[k]);
      });
      const query = qs.toString();
      setIframeModal({
        title: rotuloDaAction(action),
        src: `${backendUrl}${plugin.routePath}${ep}${query ? `?${query}` : ""}`,
        height: action.height || 600
      });
      return;
    }

    setLoading((prev) => ({ ...prev, [id]: true }));
    // Abre ja em "carregando": a chamada a IA leva segundos, e sem sinal na
    // tela o atendente clica de novo achando que nao funcionou.
    if (resultType === "jsx") setResultadoJsx({ acao: action, texto: "", carregando: true });

    // Modo evento (padrão): sem endpoint declarado, a rota é o próprio id da
    // action — o plugin só registra server.on(id, handler), sem escrever rota.
    const route = endpoint || `/${id}`;

    try {
      const { data } = await api.post(`${plugin.routePath}${route}`, context);

      const resultText = typeof data === "string" ? data : data.result || data.text || JSON.stringify(data, null, 2);

      if (resultType === "jsx") {
        // O core nao decide o que fazer com o texto — entrega ao JSX do
        // plugin, que desenha e escolhe (inserir na caixa, copiar, descartar).
        // Entrega o CORPO INTEIRO, nao so o texto: o que mais vem ali (opcoes,
        // tools consultadas) e assunto do plugin — o core nao precisa saber.
        setResultadoJsx({ acao: action, texto: resultText, dados: data, carregando: false });
      } else if (resultType === "insert-text") {
        if (onInsertText) {
          onInsertText(resultText);
        } else {
          toast.info(resultText);
        }
      } else if (resultType === "dialog" || resultType === "text" || resultType === "html") {
        setDialogTitle(rotuloDaAction(action));
        setDialogContent(resultText);
        setDialogOpen(true);
      } else if (resultType === "json") {
        toast.success(data.message || "Acao executada");
      } else {
        toast.success("Acao executada");
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || "Erro ao executar acao";
      // Sem isto o popup ficaria girando para sempre depois de um erro.
      if (resultType === "jsx") setResultadoJsx(null);
      toast.error(msg);
    } finally {
      setLoading((prev) => ({ ...prev, [id]: false }));
    }
  }, [context]);

  if (actions.length === 0) return null;

  return (
    <>
      {/*
        Resultado desenhado pelo plugin (resultType "jsx"). Fica ANTES dos
        botoes no DOM porque o JSX tipicamente sobe com `bottom: 100%`,
        ocupando o espaco acima da caixa de digitacao.

        O core so hospeda: quem desenha o balao, escolhe os botoes e decide o
        que fazer com o texto e o plugin. Antes isto era um `resultType:
        "suggestion"` com o balao desenhado AQUI — outro plugin que quisesse
        sugerir de outro jeito recebia o balao que o core tinha decidido.
      */}
      {resultadoJsx && (
        <ResultadoJsx
          jsx={resultadoJsx.acao.resultJsx}
          // `plugin` so existe dentro do laco que monta as actions; aqui ele
          // vem junto da action (cada action carrega o plugin de origem).
          pluginId={resultadoJsx.acao.plugin?.id || resultadoJsx.acao.plugin?.channel}
          texto={resultadoJsx.texto}
          dados={resultadoJsx.dados}
          carregando={resultadoJsx.carregando}
          contexto={context}
          onInserirTexto={onInsertText}
          onFechar={() => setResultadoJsx(null)}
        />
      )}

      {actions.map((action) => {
        const IconComp = iconMap[action.icon] || Extension;
        const isLoading = loading[action.id];

        // render "html": plugin desenha conteúdo arbitrário inline (sem botão,
        // sem clique). Busca o HTML disparando o evento no mount (ou usa action.html).
        // Sem endpoint declarado, o evento é o próprio id da action.
        if (action.render === "html") {
          return (
            <PluginHtmlInline
              key={action.id}
              routePath={action.plugin.routePath}
              endpoint={action.endpoint || `/${action.id}`}
              html={action.html}
              context={context}
            />
          );
        }

        if (renderAs === "menu-items") {
          return (
            <MenuItem
              key={action.id}
              onClick={() => handleAction(action)}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={18} style={{ marginRight: 8 }} />
              ) : (
                <IconComp fontSize="small" style={{ marginRight: 8 }} />
              )}
              {rotuloDaAction(action)}
            </MenuItem>
          );
        }

        if (renderAs === "text-buttons") {
          return (
            <Button
              key={action.id}
              variant="outlined"
              size="small"
              startIcon={isLoading ? <CircularProgress size={16} /> : <IconComp />}
              onClick={() => handleAction(action)}
              disabled={isLoading}
              style={{ marginRight: 8 }}
            >
              {rotuloDaAction(action)}
            </Button>
          );
        }

        return (
          <Tooltip key={action.id} title={rotuloDaAction(action)}>
            <IconButton
              size="small"
              onClick={() => handleAction(action)}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={18} />
              ) : (
                <IconComp fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        );
      })}

      {/* Dialog para mostrar resultado */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Typography
            variant="body1"
            style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
          >
            {dialogContent}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal com iframe (openType: "modal-iframe") — página servida pelo plugin */}
      <Dialog
        open={!!iframeModal}
        onClose={() => setIframeModal(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{iframeModal?.title}</DialogTitle>
        <DialogContent dividers style={{ padding: 0 }}>
          {iframeModal && (
            <iframe
              title={iframeModal.title || "Plugin"}
              src={iframeModal.src}
              style={{ width: "100%", height: iframeModal.height || 600, border: 0, display: "block" }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIframeModal(null)} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
